import { EventProgramItem } from 'components/eventView/pages/eventData';
import * as DOMPurify from 'dompurify';
import { AddToCalendarButton } from 'elements/eventView/atomic/ProgramListItem/AddToCalendarButton';
import { GoToMarkerButton } from 'elements/eventView/atomic/ProgramListItem/GoToMarkerButton';
import { ShowDetailButton } from 'elements/eventView/atomic/ProgramListItem/ShowDetailButton';
import { MarkdownContent } from 'elements/visual/MarkdownContent';
import { EFonts, EVariables } from 'libs/styles/styling';
import { removeNbspFromText } from 'libs/utils/logic/ui/markdownLogic';
import { useContext, useState } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    program: EventProgramItem;
    dateStyle: 'time' | 'datetime';
}

export const ProgramListItem = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const [showDescription, setShowDescription] = useState(false);
    const isWide = ctxEventView.isWide;

    const program = props.program;
    const {
        programId,
        title,
        startDateTime,
        endDateTime,
        placeName,
        storyIds,
        storyIdsToOpen,
        markerId
    } = props.program ?? program;

    const description = DOMPurify.sanitize(removeNbspFromText(props.program.description), {
        ALLOWED_TAGS: []
    });

    const getDateText = () => {
        const month = startDateTime?.substring(5, 7);
        const day = startDateTime?.substring(8, 10);

        let result = props.dateStyle === 'datetime' ? `${day}.${month} ` : '';

        const from = startDateTime.substring(11, 16);
        if (endDateTime) {
            const to = endDateTime?.substring(11, 16);
            result += `${from} - ${to}`;
        } else {
            result += `${from}`;
        }
        if (placeName) {
            result += ` · ${placeName}`;
        }
        return result;
    };
    const dateText = getDateText();

    const partialDescriptionLimit = isWide ? 240 : 120;

    const isPartialVisible = description?.length > partialDescriptionLimit && !showDescription;

    const backgroundColor = ctxEventView.isEmbed
        ? ctxEventView.eventData?.programSettings?.embedItemBackgroundColor ?? '#eaf0f5'
        : '#eaf0f5';

    return (
        <S_ProgramListItem
            $isWide={isWide}
            className={props.className}
            $backgroundColor={backgroundColor}
        >
            <S_DateRow>
                <S_DateRowText $isWide={isWide}>{dateText}</S_DateRowText>
                {storyIds?.length > 0 && <S_Fill />}
                {markerId && <S_Fill />}
            </S_DateRow>
            <S_Title $isWide={isWide}>{title}</S_Title>
            <S_Description
                $isWide={isWide}
                onClick={() => {
                    setShowDescription(!showDescription);
                }}
                text={
                    isPartialVisible
                        ? description?.substring(0, partialDescriptionLimit)
                        : description
                }
                showReadMore={isPartialVisible}
            />
            <S_ButtonsContainer $isWide={isWide}>
                <ShowDetailButton
                    storyIds={storyIdsToOpen}
                    title={title}
                    markerId={markerId}
                    programId={programId}
                />
                {!ctxEventView.isEmbed && <GoToMarkerButton markerId={markerId} title={title} />}
                <AddToCalendarButton
                    programId={programId}
                    title={title}
                    hasEndDate={!!endDateTime}
                    storyIds={storyIdsToOpen}
                />
            </S_ButtonsContainer>
        </S_ProgramListItem>
    );
};

const S_ProgramListItem = styled.div<{ $isWide: boolean; $backgroundColor: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 20px 20px;
    border-radius: ${EVariables.Radius1};
    background-color: ${(props) => props.$backgroundColor};
    ${(props) => {
        if (props.$isWide) {
            return css`
                border-radius: ${EVariables.Radius2};
                padding: 30px 40px;
            `;
        }
    }}
`;

const S_Title = styled.div<{ $isWide: boolean }>`
    color: #041321;
    font-size: 16px;
    font-family: ${EFonts.Bold};
    margin: 2px 0 10px 0;
    ${(props) => {
        if (props.$isWide) {
            return css`
                margin: 10px 0 15px 0;
                font-size: 18px;
            `;
        }
    }}
`;
const S_Description = styled(MarkdownContent)<{ $isWide: boolean }>`
    color: #4a4a4a;
    font-size: 14px;
    font-family: ${EFonts.InterMedium};
    line-height: 25px;
    ${(props) => {
        if (props.$isWide) {
            return css`
                font-size: 16px;
                line-height: 31px;
            `;
        }
    }}
`;

const S_ButtonsContainer = styled.div<{ $isWide: boolean }>`
    position: absolute;
    right: 10px;
    top: 10px;
    gap: 15px;
    display: flex;
    flex-direction: row;
    ${(props) => {
        if (props.$isWide) {
            return css`
                gap: 20px;
                right: 20px;
                top: 20px;
            `;
        }
    }}
`;

const S_DateRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 12px;
`;

const S_DateRowText = styled.div<{ $isWide: boolean }>`
    color: #4a4a4a;
    font-size: 14px;
    font-family: ${EFonts.InterMedium};
    width: 100%;
    flex-shrink: 1;
    line-height: 22px;
    ${(props) => {
        if (props.$isWide) {
            return css`
                font-size: 15px;
            `;
        }
    }}
`;

const S_Fill = styled.div`
    display: flex;
    flex-shrink: 0;
    width: 45px;
    height: 1px;
`;
